import React, { useState, useEffect, useCallback } from 'react';
import {
  MessageSquare, Search, RefreshCw, Eye, X, Copy, Check,
  FileText, Tag, Shield, ChevronDown
} from 'lucide-react';
import api from '../../services/api';

const cardStyle = { border: '1px solid rgba(99, 102, 241, 0.08)', boxShadow: '0 1px 3px rgba(99, 102, 241, 0.06)' };

const TYPE_COLORS = {
  transactional: { bg: '#dbeafe', text: '#1d4ed8', border: '#93c5fd' },
  service_implicit: { bg: '#dcfce7', text: '#15803d', border: '#86efac' },
  service_explicit: { bg: '#fef3c7', text: '#b45309', border: '#fcd34d' },
  promotional: { bg: '#fce7f3', text: '#be185d', border: '#f9a8d4' },
};

const SMSTemplateManagement = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [entityName, setEntityName] = useState('');
  const [viewTemplate, setViewTemplate] = useState(null);
  const [copied, setCopied] = useState('');

  const fetchTemplates = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/admin/sms-templates/platform');
      setTemplates(res.data.templates || []);
      setEntityName(res.data.entity || '');
    } catch (err) {
      console.error('Failed to fetch SMS templates:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

  const copyToClipboard = (text, key) => {
    navigator.clipboard.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(''), 2000);
  };

  const filtered = templates.filter(t => {
    const matchSearch = !search ||
      t.template_name.toLowerCase().includes(search.toLowerCase()) ||
      t.template_id.toLowerCase().includes(search.toLowerCase()) ||
      t.template_content.toLowerCase().includes(search.toLowerCase()) ||
      (t.header_name && t.header_name.toLowerCase().includes(search.toLowerCase()));
    const matchType = !filterType || t.template_type === filterType;
    const matchStatus = !filterStatus || (filterStatus === 'active' ? t.is_active : !t.is_active);
    return matchSearch && matchType && matchStatus;
  });

  const typeStats = templates.reduce((acc, t) => {
    acc[t.template_type] = (acc[t.template_type] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <MessageSquare className="w-6 h-6" style={{ color: '#4f46e5' }} /> SMS Template Management
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            {templates.length} templates · <span className="font-medium text-indigo-600">{entityName}</span> · Read-only list for alert purposes
          </p>
        </div>
        <button onClick={fetchTemplates} className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium text-white shadow-sm" style={{ background: 'linear-gradient(135deg, #4f46e5, #06b6d4)' }}>
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="bg-white rounded-xl p-4" style={cardStyle}>
          <div className="text-2xl font-bold text-gray-900">{templates.length}</div>
          <div className="text-xs text-gray-500 mt-1">Total Templates</div>
        </div>
        {Object.entries(typeStats).map(([type, count]) => {
          const tc = TYPE_COLORS[type] || { bg: '#f3f4f6', text: '#374151', border: '#d1d5db' };
          return (
            <div key={type} className="bg-white rounded-xl p-4" style={cardStyle}>
              <div className="text-2xl font-bold" style={{ color: tc.text }}>{count}</div>
              <div className="text-xs text-gray-500 mt-1 capitalize">{type.replace('_', ' ')}</div>
            </div>
          );
        })}
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input type="text" placeholder="Search by name, ID, content, header..." value={search} onChange={e => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200"
            style={{ borderColor: 'rgba(99, 102, 241, 0.15)' }} />
        </div>
        <select value={filterType} onChange={e => setFilterType(e.target.value)}
          className="px-3 py-2 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200"
          style={{ borderColor: 'rgba(99, 102, 241, 0.15)' }}>
          <option value="">All Types</option>
          <option value="transactional">Transactional</option>
          <option value="service_implicit">Service Implicit</option>
          <option value="service_explicit">Service Explicit</option>
          <option value="promotional">Promotional</option>
        </select>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
          className="px-3 py-2 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200"
          style={{ borderColor: 'rgba(99, 102, 241, 0.15)' }}>
          <option value="">All Status</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
      </div>

      {/* Template Table */}
      <div className="bg-white rounded-xl overflow-hidden" style={cardStyle}>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr style={{ background: 'rgba(99, 102, 241, 0.03)', borderBottom: '1px solid rgba(99, 102, 241, 0.08)' }}>
                <th className="text-left py-3 px-4 text-[10px] font-bold text-gray-500 uppercase tracking-wider">Template ID</th>
                <th className="text-left py-3 px-4 text-[10px] font-bold text-gray-500 uppercase tracking-wider">Name</th>
                <th className="text-left py-3 px-4 text-[10px] font-bold text-gray-500 uppercase tracking-wider">Header</th>
                <th className="text-left py-3 px-4 text-[10px] font-bold text-gray-500 uppercase tracking-wider">Type</th>
                <th className="text-left py-3 px-4 text-[10px] font-bold text-gray-500 uppercase tracking-wider">Status</th>
                <th className="text-left py-3 px-4 text-[10px] font-bold text-gray-500 uppercase tracking-wider">Content Preview</th>
                <th className="text-center py-3 px-4 text-[10px] font-bold text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                [...Array(5)].map((_, i) => (
                  <tr key={i} className="border-b" style={{ borderColor: 'rgba(99,102,241,0.04)' }}>
                    {[...Array(7)].map((_, j) => (
                      <td key={j} className="py-3 px-4"><div className="h-4 bg-gray-100 rounded animate-pulse" style={{ width: `${60 + Math.random() * 40}%` }} /></td>
                    ))}
                  </tr>
                ))
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan="7" className="py-12 text-center">
                    <MessageSquare className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                    <p className="text-gray-500 font-medium">No templates found</p>
                    <p className="text-xs text-gray-400 mt-1">Try adjusting your search or filters</p>
                  </td>
                </tr>
              ) : (
                filtered.map(t => {
                  const tc = TYPE_COLORS[t.template_type] || { bg: '#f3f4f6', text: '#374151', border: '#d1d5db' };
                  return (
                    <tr key={t.id} className="border-b hover:bg-gray-50 transition-colors" style={{ borderColor: 'rgba(99,102,241,0.04)' }}>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-1">
                          <span className="text-xs font-mono text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded">{t.template_id}</span>
                          <button onClick={() => copyToClipboard(t.template_id, `tid-${t.id}`)}
                            className="p-0.5 rounded hover:bg-gray-200 text-gray-400 hover:text-gray-600">
                            {copied === `tid-${t.id}` ? <Check className="w-3 h-3 text-green-500" /> : <Copy className="w-3 h-3" />}
                          </button>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <span className="text-sm font-medium text-gray-900">{t.template_name}</span>
                      </td>
                      <td className="py-3 px-4">
                        <span className="text-xs font-semibold text-gray-700 bg-gray-100 px-2 py-0.5 rounded">{t.header_name || '-'}</span>
                      </td>
                      <td className="py-3 px-4">
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase" style={{ background: tc.bg, color: tc.text, border: `1px solid ${tc.border}` }}>
                          {t.template_type.replace('_', ' ')}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <span className={`w-2.5 h-2.5 rounded-full inline-block ${t.is_active ? 'bg-green-500' : 'bg-gray-300'}`} />
                        <span className={`ml-1.5 text-xs font-medium ${t.is_active ? 'text-green-700' : 'text-gray-500'}`}>
                          {t.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <p className="text-xs text-gray-500 truncate max-w-[250px]" title={t.template_content}>
                          {t.template_content.slice(0, 80)}{t.template_content.length > 80 ? '...' : ''}
                        </p>
                      </td>
                      <td className="py-3 px-4 text-center">
                        <button onClick={() => setViewTemplate(t)}
                          className="p-1.5 rounded-lg hover:bg-indigo-50 text-gray-400 hover:text-indigo-600 transition-colors" title="View Details">
                          <Eye className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        {!loading && filtered.length > 0 && (
          <div className="px-4 py-3 text-xs text-gray-400" style={{ borderTop: '1px solid rgba(99,102,241,0.06)', background: 'rgba(99,102,241,0.02)' }}>
            Showing {filtered.length} of {templates.length} templates
          </div>
        )}
      </div>

      {/* View Template Modal */}
      {viewTemplate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.5)' }} onClick={() => setViewTemplate(null)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4" style={{ borderBottom: '1px solid rgba(99,102,241,0.1)', background: 'rgba(99,102,241,0.02)' }}>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #4f46e5, #06b6d4)' }}>
                  <FileText className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-gray-900">{viewTemplate.template_name}</h3>
                  <p className="text-xs text-gray-500">{viewTemplate.entity_name}</p>
                </div>
              </div>
              <button onClick={() => setViewTemplate(null)} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Template ID</label>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-sm font-mono text-indigo-600 bg-indigo-50 px-3 py-1 rounded-lg">{viewTemplate.template_id}</span>
                    <button onClick={() => copyToClipboard(viewTemplate.template_id, 'modal-tid')}
                      className="p-1 rounded hover:bg-gray-100 text-gray-400">
                      {copied === 'modal-tid' ? <Check className="w-3.5 h-3.5 text-green-500" /> : <Copy className="w-3.5 h-3.5" />}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Header</label>
                  <div className="text-sm font-semibold text-gray-800 mt-1">{viewTemplate.header_name || '-'}</div>
                </div>
                <div>
                  <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Type</label>
                  <div className="mt-1">
                    {(() => {
                      const tc = TYPE_COLORS[viewTemplate.template_type] || { bg: '#f3f4f6', text: '#374151', border: '#d1d5db' };
                      return <span className="px-2 py-0.5 rounded-full text-xs font-bold uppercase" style={{ background: tc.bg, color: tc.text }}>{viewTemplate.template_type.replace('_', ' ')}</span>;
                    })()}
                  </div>
                </div>
                <div>
                  <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Status</label>
                  <div className="mt-1">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${viewTemplate.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                      {viewTemplate.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>
              </div>
              <div>
                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Template Content</label>
                <div className="mt-2 relative">
                  <div className="bg-gray-50 rounded-xl p-4 text-sm text-gray-800 font-mono leading-relaxed whitespace-pre-wrap" style={{ border: '1px solid rgba(99,102,241,0.08)' }}>
                    {viewTemplate.template_content}
                  </div>
                  <button onClick={() => copyToClipboard(viewTemplate.template_content, 'modal-content')}
                    className="absolute top-2 right-2 p-1.5 rounded-lg bg-white shadow-sm hover:bg-gray-100 text-gray-400 border border-gray-200">
                    {copied === 'modal-content' ? <Check className="w-3.5 h-3.5 text-green-500" /> : <Copy className="w-3.5 h-3.5" />}
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4 text-xs text-gray-500">
                <div>
                  <span className="font-medium">Created:</span> {new Date(viewTemplate.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                </div>
                <div>
                  <span className="font-medium">Updated:</span> {new Date(viewTemplate.updated_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SMSTemplateManagement;
